import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { saveAs } from 'file-saver';

import { Period } from 'src/app/menu/smart-review/core/utils/period.util';
import { CostsService } from './costs.service';
import { CostPeriodFilters } from './interfaces/costs.interface';
import { buildCostsCsv, costsCsvFilename } from './utils/costs-csv.util';

/** Teto de linhas do backend. Bater nele significa que a exportação veio truncada. */
export const COSTS_EXPORT_LIMIT = 50000;

/** Resultado do download, para a tela decidir a mensagem. */
export interface CostsExportResult {
  rows: number;
  /** True quando bateu o teto do backend — o arquivo está incompleto. */
  truncated: boolean;
}

/**
 * Monta e baixa o CSV das execuções. Vive num serviço, e não num componente, porque a
 * exportação é disparada por uma ação do `po-page-default` — o botão é do PO, não nosso.
 *
 * O backend devolve JSON justamente para o front decidir separador, decimal e encoding:
 * só ele sabe o locale do destinatário.
 */
@Injectable()
export class CostsExportService {
  constructor(private costsService: CostsService) {}

  /**
   * Exporta o recorte da tela — período e filtros. O que se exporta é o que se vê:
   * exportar sem os filtros entregaria uma planilha que não bate com nenhum número
   * da tela.
   */
  export(
    filters: CostPeriodFilters,
    period: Period,
    currency: string,
    areaNames: Map<string, string>
  ): Observable<CostsExportResult> {
    return this.costsService.getExport(filters, COSTS_EXPORT_LIMIT).pipe(
      map(rows => rows ?? []),
      tap(rows => {
        if (!rows.length) { return; }
        const csv = buildCostsCsv(rows, period, currency, areaNames);
        // O BOM é obrigatório: sem ele o Excel pt-BR lê o arquivo como latin-1 e os
        // acentos viram "Ã§". O charset no type não substitui o BOM.
        saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), costsCsvFilename(period));
      }),
      map(rows => ({ rows: rows.length, truncated: rows.length >= COSTS_EXPORT_LIMIT })),
      catchError(error => throwError(() => error))
    );
  }
}
